import React, { useState, useEffect, useMemo } from "react";

import { 
  Button, 
  Box, 
  Avatar, 
  Collapse, 
  TextField, 
  Grid, 
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Typography,
  Pagination
} from "@mui/material";
import { Feed, List } from "semantic-ui-react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

const ITEMS_PER_PAGE = 10;
const ALL_ACTIVITIES = "ALL";
const ALL_USERS = "ALL_USERS";
const ACTIVITY_FILTERS = [
  { value: ALL_ACTIVITIES, label: "All" },
  // { value: "CREATE", label: "Create" },
  { value: "EDIT", label: "Update" },
];

export default function ActivityHistoryInfoSection ({ organizationActivities }) {
  const [filteredActivities, setFilteredActivities] = useState(organizationActivities);
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [currentFilter, setCurrentFilter] = useState(ALL_ACTIVITIES);
  const [selectedUser, setSelectedUser] = useState(ALL_USERS);
  const [showActivityDetail, setShowActivityDetail] = useState({ 0: true });
  const [currentPage, setCurrentPage] = useState(1);
  
  // Function to reset all filters
  const resetFilters = () => {
    setSearchTerm("");
    setFromDate("");
    setToDate("");
    setCurrentFilter(ALL_ACTIVITIES);
    setSelectedUser(ALL_USERS);
    setCurrentPage(1);
  };
  
  // Extract unique users from activities
  const uniqueUsers = useMemo(() => {
    const uniqueUsersSet = new Set();
    organizationActivities.forEach(activity => {
      if (activity.userFullName) {
        uniqueUsersSet.add(activity.userFullName);
      }
    });
    return [ALL_USERS, ...Array.from(uniqueUsersSet)];
  }, [organizationActivities]);
  
  // Apply all filters whenever any filter changes
  useEffect(() => {
    let result = organizationActivities;
    
    // Filter by search term
    if (searchTerm) {
      result = result.filter(activity => {
        const field = JSON.parse(activity.field || "{}");
        const activityText = JSON.stringify(field).toLowerCase();
        const userNameMatch = activity.userFullName?.toLowerCase().includes(searchTerm.toLowerCase());
        const contentMatch = activityText.includes(searchTerm.toLowerCase());
        return userNameMatch || contentMatch;
      });
    }
    
    // Filter by date range
    if (fromDate) {
      const fromTimestamp = new Date(fromDate).getTime();
      result = result.filter(activity => new Date(activity.createdAt).getTime() >= fromTimestamp);
    }
    
    if (toDate) {
      // Set to end of day for the "to" date
      const toTimestamp = new Date(toDate);
      toTimestamp.setHours(23, 59, 59, 999);
      result = result.filter(activity => new Date(activity.createdAt).getTime() <= toTimestamp.getTime());
    }
    
    // Filter by activity type
    if (currentFilter !== ALL_ACTIVITIES) {
      result = result.filter(activity => activity.action === currentFilter);
    }
    
    // Filter by user
    if (selectedUser !== ALL_USERS) {
      result = result.filter(activity => activity.userFullName === selectedUser);
    }
    
    setFilteredActivities(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [organizationActivities, searchTerm, fromDate, toDate, currentFilter, selectedUser]);
  
  // Check if any filters are active
  const hasActiveFilters = searchTerm || fromDate || toDate || currentFilter !== ALL_ACTIVITIES || selectedUser !== ALL_USERS;
  
  const handleFilterChange = (event, newValue) => {
    setCurrentFilter(newValue);
  };
  
  const handleUserChange = (event) => {
    setSelectedUser(event.target.value);
  };
  
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };
  
  // Calculate pagination
  const totalPages = Math.ceil(filteredActivities.length / ITEMS_PER_PAGE);
  const paginatedActivities = filteredActivities.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  
  // Display no activity message in case when there is nothing in activity log
  if (organizationActivities.length < 1) {
    return (
      <Box>
        <span style={{ fontWeight: "600" }}>No Activity History</span>
      </Box>
    )
  }

  return (
    <Box className={"other-info-section"}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <span style={{ fontWeight: "600" }}>Activity History</span>
        <Button
          variant="outlined"
          startIcon={<RestartAltIcon />}
          size="small"
          disabled={!hasActiveFilters}
          onClick={resetFilters}
        >
          Reset filters
        </Button>
      </Box>
      
      {/* Search, User and Date Filters */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="Search activities"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="user-filter-label">Filter by User</InputLabel>
            <Select
              labelId="user-filter-label"
              id="user-filter"
              value={selectedUser}
              label="Filter by User"
              onChange={handleUserChange}
            >
              <MenuItem value={ALL_USERS}>All Users</MenuItem>
              {uniqueUsers.filter(user => user !== ALL_USERS).map((user) => (
                <MenuItem key={user} value={user}>
                  {user}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                id="from-date"
                label="From Date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                id="to-date"
                label="To Date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      
      {/* Activity Type Filters */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={currentFilter} 
          onChange={handleFilterChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {ACTIVITY_FILTERS.map(filter => (
            <Tab key={filter.value} label={filter.label} value={filter.value} />
          ))}
        </Tabs>
      </Box>

      {/* Activities Feed */}
      <Box sx={{ overflowY: "auto", maxHeight: "600px", "&::-webkit-scrollbar": { width: "2px" } }}>
        {paginatedActivities.length > 0 ? (
          <Feed>
            {paginatedActivities.map((activity, index) => {
              let updates = JSON.parse(activity.field || "{}");
              const nameUpdate = updates.name;
              const vendorLabelUpdate = updates.vendorLabel;
              const projectLabelUpdate = updates.projectLabel;
              const certificateHolderUpdate = updates.certificateHolder;
              const certificateHolderAddress1 = updates.certificateHolderAddress1;
              const certificateHolderAddress2 = updates.certificateHolderAddress2;
              const certificateHolderCity = updates.certificateHolderCity;
              const certificateHolderZip = updates.certificateHolderZip;
              const certificateHolderState = updates.certificateHolderState;
              const clientLabelUpdate = updates.clientLabel;
              const useDefaultProject = updates.useDefaultProject;
              const usePrimaryNotificationEmailAddress = updates.usePrimaryNotificationEmailAddress;
              const primaryNotificationEmailAddress = updates.primaryNotificationEmailAddress;
              const adminName = updates.adminName;
              const adminEmail = updates.adminEmail;
              const website = updates.website;
              const city = updates.city;
              const state = updates.state;
              const zip = updates.zip;
              const address1 = updates.address1;
              const address2 = updates.address2;
              const phone = updates.phone;
              const tableRowsPerPage = updates.tableRowsPerPage;
              const logoUpdate = updates.logo;
              const userInviteUpdate = updates.user_invited;
              const userDeleteUpdate = updates.user_deleted;
              const userDisableUpdate = updates.user_disabled;
              const userEnableUpdate = updates.user_enabled;
              const emailTemplateUpdate = { templates: updates.templates };
              const clientCoveragesUpdate = { clientCoverages: updates.clientCoverages };
              const updateSections = [];
              let updatedSectionDesc;
              nameUpdate && updateSections.push("name");
              vendorLabelUpdate && updateSections.push("Vendor's Label");
              projectLabelUpdate && updateSections.push("Project's Label");
              certificateHolderUpdate && updateSections.push("Global Certificate Holder's Name");
              certificateHolderAddress1 && updateSections.push("Global Certificate Holder's Address1");
              certificateHolderAddress2 && updateSections.push("Global Certificate Holder's Address2");
              certificateHolderCity && updateSections.push("Global Certificate Holder's City");
              certificateHolderZip && updateSections.push("Global Certificate Holder's Zip");
              certificateHolderState && updateSections.push("Global Certificate Holder's State");
              clientLabelUpdate && updateSections.push("Client's Label");
              useDefaultProject && updateSections.push("enforce Default Project's coverages");
              usePrimaryNotificationEmailAddress && updateSections.push("enforce Primary Notification Email Address");
              primaryNotificationEmailAddress && updateSections.push("Primary Notification Email Address");
              adminName && updateSections.push("Admin Name");
              adminEmail && updateSections.push("Admin Email Address");
              website && updateSections.push("Website URL");
              city && updateSections.push("City");
              zip && updateSections.push("ZIP");
              state && updateSections.push("State");
              phone && updateSections.push("Phone");
              address1 && updateSections.push("Address 1");
              address2 && updateSections.push("Address 2");
              tableRowsPerPage && updateSections.push("Table Rows Per Page");
              logoUpdate && updateSections.push("logo");
              (userInviteUpdate || userDeleteUpdate || userDisableUpdate || userEnableUpdate) && updateSections.push("user");
              emailTemplateUpdate.templates && updateSections.push("template");
              clientCoveragesUpdate.clientCoverages && updateSections.push("client coverages");

              if (updateSections.length === 1) {
                updatedSectionDesc = updateSections[0];
              } else if (updateSections.length === 2) {
                updatedSectionDesc = `${updateSections[0]} and ${updateSections[1]}`;
              } else {
                updatedSectionDesc = ` : `;
              }

              const actualIndex = (currentPage - 1) * ITEMS_PER_PAGE + index;

              return (
                <Feed.Event key={"activity-" + index}>
                  <Feed.Label>
                    <Avatar>
                      {String(activity.userFullName)
                        .split(" ")
                        .map((part) => part.charAt(0))
                        .join("")
                        .toUpperCase()}
                    </Avatar>
                  </Feed.Label>
                  <Feed.Content>
                    <Feed.Date>{new Date(activity.createdAt).toLocaleString("en-us")}</Feed.Date>
                    <Feed.Summary style={{ display: "flex", alignItems: "baseline" }}>
                      <Box>
                        <span style={{ fontWeight: "600" }}>{activity.userFullName}</span>
                        {activity.action === "CREATE" ? ` created organization` : ` updated organization's ${updatedSectionDesc}`}
                      </Box>
                      <Button
                        size="small"
                        sx={{ ml: "auto", flexShrink: 0 }}
                        onClick={() =>
                          setShowActivityDetail({
                            ...showActivityDetail,
                            [actualIndex]: !showActivityDetail[actualIndex],
                          })
                        }
                      >
                        {`${!!showActivityDetail[actualIndex] ? "Hide" : "Show"} details`}
                      </Button>
                    </Feed.Summary>
                    {!!Object.keys(updates).length && (
                      <Collapse in={!!showActivityDetail[actualIndex]}>
                        <Feed.Extra text>
                          <List bulleted={true}>
                            {Object.entries({
                              ...(nameUpdate || {}),
                              ...(vendorLabelUpdate || {}),
                              ...(projectLabelUpdate || {}),
                              ...(certificateHolderUpdate || {}),
                              ...(certificateHolderAddress1 || {}),
                              ...(certificateHolderAddress2 || {}),
                              ...(certificateHolderCity || {}),
                              ...(certificateHolderState || {}),
                              ...(certificateHolderZip || {}),
                              ...(useDefaultProject || {}),
                              ...(usePrimaryNotificationEmailAddress || {}),
                              ...(primaryNotificationEmailAddress || {}),
                              ...(adminName || {}),
                              ...(adminEmail || {}),
                              ...(website || {}),
                              ...(city || {}),
                              ...(state || {}),
                              ...(zip || {}),
                              ...(address1 || {}),
                              ...(address2 || {}),
                              ...(phone || {}),
                              ...(tableRowsPerPage || {}),
                              ...(logoUpdate || {}),
                              ...(userInviteUpdate || {}),
                              ...(userDeleteUpdate || {}),
                              ...(userDisableUpdate || {}),
                              ...(userEnableUpdate || {}),
                              ...(userInviteUpdate || {}),
                              ...(userDeleteUpdate || {}),
                              ...(userDisableUpdate || {}),
                              ...(userEnableUpdate || {}),
                              ...(emailTemplateUpdate.templates ? emailTemplateUpdate : {}),
                              ...(clientCoveragesUpdate.clientCoverages ? clientCoveragesUpdate : []),
                            }).map(([fieldName, updates]) => {
                              //user management
                              if (updates?.next?.email) {
                                return (
                                  <List.Item key={`${activity.id}_${fieldName}`}>
                                    <p style={{ textTransform: "capitalize", fontWeight: "600" }}>{fieldName.replace("_", " ")}</p>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                      {updates.next ? (
                                        <p style={{ flexBasis: "50%", flexGrow: 0, overflowWrap: "anywhere" }}>
                                          <span style={{ color: "darkgray" }}>Email: </span>
                                          {updates.next?.email} <br />
                                          <span style={{ color: "darkgray" }}>Role: </span> {updates.next?.role}
                                        </p>
                                      ) : (
                                        <p style={{ color: "darkgray" }}>{"blank"}</p>
                                      )}
                                    </Box>
                                  </List.Item>
                                );
                              } else if (fieldName === "templates") {
                                return Object.entries(updates).map(([tempKey, tempVal]) => {
                                  return (
                                    <List.Item key={`${activity.id}_${fieldName}_${tempKey}`}>
                                      <p style={{ textTransform: "capitalize", textDecoration: "underline", fontWeight: "600" }}>{tempKey}</p>
                                      {Object.entries(tempVal).map(([key, val]) => {
                                        return (
                                          <div key={`${activity.id}_${fieldName}_${tempKey}_${key}`}>
                                            <p style={{ textTransform: "capitalize", fontWeight: "600", paddingTop: 20 }}>{key}</p>
                                            <br />
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                              {val.prev ? (
                                                <p style={{ flexBasis: "50%", flexGrow: 0, overflowWrap: "anywhere" }}>{val.prev}</p>
                                              ) : (
                                                <p style={{ color: "darkgray" }}>{"blank"}</p>
                                              )}
                                              <ArrowForwardIcon sx={{ fontSize: 16, ml: 1, mr: 1 }} />
                                              {val.next ? (
                                                <p style={{ flexBasis: "50%", flexGrow: 0, overflowWrap: "anywhere" }}>{val.next}</p>
                                              ) : (
                                                <p style={{ color: "darkgray" }}>{"blank"}</p>
                                              )}
                                            </Box>
                                          </div>
                                        );
                                      })}
                                    </List.Item>
                                  );
                                });
                              } else if (fieldName === "clientCoverages" && Array.isArray(updates)) {
                                return updates.map((update, updateIndex) => {
                                  return Object.entries(update).map(([updateKey, { prev, next }]) => {
                                    if (!prev) {
                                      return (
                                        <List.Item key={`${activity.id}_${fieldName}_${updateIndex}`}>
                                          <p style={{ textTransform: "capitalize", fontWeight: "600" }}>Created {updateKey} coverage</p>
                                        </List.Item>
                                      );
                                    } else if (!next) {
                                      return (
                                        <List.Item key={`${activity.id}_${fieldName}_${updateIndex}`}>
                                          <p style={{ textTransform: "capitalize", fontWeight: "600" }}>Deleted {updateKey} coverage</p>
                                        </List.Item>
                                      );
                                    } else {
                                      return (
                                        <List.Item key={`${activity.id}_${fieldName}_${updateIndex}`}>
                                          <p style={{ textTransform: "capitalize", fontWeight: "600" }}>{updateKey} coverage</p>
                                          {Object.entries(prev).map(([key, value]) => {
                                            return (
                                              <div key={`${activity.id}_${fieldName}_${key}`}>
                                                <p style={{ textTransform: "capitalize", fontWeight: "600", paddingTop: 20 }}>
                                                  {key.replace(/([a-z])([A-Z])/g, "$1 $2")}
                                                </p>
                                                <br />
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                  {value ? (
                                                    <p style={{ flexBasis: "50%", flexGrow: 0, overflowWrap: "anywhere" }}>{JSON.stringify(value)}</p>
                                                  ) : (
                                                    <p style={{ color: "darkgray" }}>{"blank"}</p>
                                                  )}
                                                  <ArrowForwardIcon sx={{ fontSize: 16, ml: 1, mr: 1 }} />
                                                  {next[key] ? (
                                                    <p style={{ flexBasis: "50%", flexGrow: 0, overflowWrap: "anywhere" }}>{JSON.stringify(next[key])}</p>
                                                  ) : (
                                                    <p style={{ color: "darkgray" }}>{"blank"}</p>
                                                  )}
                                                </Box>
                                              </div>
                                            );
                                          })}
                                        </List.Item>
                                      );
                                    }
                                  });
                                });
                              } else {
                                return (
                                  <List.Item key={`${activity.id}_${fieldName}`}>
                                    <p style={{ textTransform: "capitalize", fontWeight: "600" }}>{fieldName}</p>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                      {updates.prev ? (
                                        <p style={{ flexBasis: "50%", flexGrow: 0, overflowWrap: "anywhere" }}>{updates.prev}</p>
                                      ) : (
                                        <p style={{ color: "darkgray" }}>{"blank"}</p>
                                      )}
                                      <ArrowForwardIcon sx={{ fontSize: 16, ml: 1, mr: 1 }} />
                                      {updates.next ? (
                                        <p style={{ flexBasis: "50%", flexGrow: 0, overflowWrap: "anywhere" }}>{updates.next}</p>
                                      ) : (
                                        <p style={{ color: "darkgray" }}>{"blank"}</p>
                                      )}
                                    </Box>
                                  </List.Item>
                                );
                              }
                            })}
                          </List>
                        </Feed.Extra>
                      </Collapse>
                    )}
                  </Feed.Content>
                </Feed.Event>
              );
            })}
          </Feed>
        ) : (
          <Typography variant="body1" color="text.secondary" align="center" my={4}>
            No activities found matching your filters.
          </Typography>
        )}
      </Box>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination 
            count={totalPages} 
            page={currentPage} 
            onChange={handlePageChange} 
            color="primary" 
          />
        </Box>
      )}
    </Box>
  );
}
